import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Users, Loader2, CheckCircle2, AlertTriangle, Building2, Shield, LogIn
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('texhub_token') || sessionStorage.getItem('texhub_token')}`,
  'Content-Type': 'application/json'
});

const JoinTeamPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(true);
  const [invitation, setInvitation] = useState(null);
  const [error, setError] = useState(null);
  const [joining, setJoining] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    let isMounted = true;
    
    const loadInvitation = async () => {
      try {
        const res = await fetch(`${API_URL}/api/team/invitation/${token}`);
        
        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.detail || 'Invitación no válida');
        }

        if (isMounted) setInvitation(await res.json());
      } catch (err) {
        if (isMounted) setError(err.message);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    
    if (token) {
      loadInvitation();
    }
    
    return () => { isMounted = false; };
  }, [token]);

  const fetchInvitation = async () => {
    try {
      const res = await fetch(`${API_URL}/api/team/invitation/${token}`);
      
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Invitación no válida');
      }

      setInvitation(await res.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    if (!isAuthenticated) {
      // Store token and redirect to login
      sessionStorage.setItem('pending_invitation', token);
      navigate('/login?redirect=join');
      return;
    }

    setJoining(true);
    try {
      const res = await fetch(`${API_URL}/api/team/accept/${token}`, {
        method: 'POST',
        headers: getAuthHeaders()
      });

      const data = await res.json();

      if (res.ok) {
        setSuccess(true);
        toast.success('¡Te has unido al equipo!');
        setTimeout(() => {
          window.location.href = '/dashboard';
        }, 2000);
      } else {
        toast.error(data.detail || 'Error al unirse');
      }
    } catch (error) {
      toast.error('Error al procesar la invitación');
    } finally {
      setJoining(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-emerald-600 mx-auto mb-4" />
          <p className="text-slate-600">Verificando invitación...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center">
          <CardContent className="pt-8 pb-8">
            <AlertTriangle className="w-16 h-16 text-orange-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Invitación no válida</h2>
            <p className="text-slate-600 mb-6">{error}</p>
            <Button asChild variant="outline">
              <Link to="/">Ir al inicio</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center">
          <CardContent className="pt-8 pb-8">
            <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 mb-2">¡Bienvenido al equipo!</h2>
            <p className="text-slate-600 mb-6">
              Te has unido exitosamente a <strong>{invitation?.company_name}</strong>
            </p>
            <p className="text-sm text-slate-500">Redirigiendo al dashboard...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardContent className="pt-8 pb-8">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Users className="w-8 h-8 text-emerald-600" />
            </div>
            <h2 className="text-xl font-semibold text-slate-900 mb-2">
              Invitación a unirse
            </h2>
            <p className="text-slate-600">
              <strong>{invitation?.invited_by_name}</strong> te invita a unirte al equipo
            </p>
          </div>

          <div className="bg-slate-50 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <Building2 className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="font-semibold text-slate-900">{invitation?.company_name}</p>
                <p className="text-sm text-slate-500">Empresa en TexHub</p>
              </div>
            </div>

            <div className="border-t pt-4">
              <p className="text-sm font-medium text-slate-700 flex items-center gap-2 mb-2">
                <Shield className="w-4 h-4" />
                Permisos que tendrás:
              </p>
              <div className="flex flex-wrap gap-1">
                {invitation?.permissions?.map(p => {
                  const labels = {
                    view_catalog: 'Ver catálogo',
                    edit_products: 'Editar productos',
                    view_quotes: 'Ver cotizaciones',
                    respond_quotes: 'Responder cotizaciones',
                    view_messages: 'Ver mensajes',
                    send_messages: 'Enviar mensajes',
                    view_stats: 'Ver estadísticas',
                    use_texvision: 'Usar TexVision AI',
                    manage_orders: 'Gestionar pedidos',
                    view_surplus: 'Ver excedentes',
                    edit_surplus: 'Editar excedentes',
                  };
                  return (
                    <Badge key={p} variant="outline" className="text-xs">
                      {labels[p] || p}
                    </Badge>
                  );
                })}
              </div>
            </div>
          </div>

          {isAuthenticated ? (
            <div className="space-y-3">
              {user?.email?.toLowerCase() !== invitation?.email?.toLowerCase() && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-sm">
                  <p className="text-orange-800">
                    <AlertTriangle className="w-4 h-4 inline mr-1" />
                    Esta invitación es para <strong>{invitation?.email}</strong>.
                    Estás conectado como <strong>{user?.email}</strong>.
                  </p>
                </div>
              )}
              <Button 
                className="w-full" 
                style={{ backgroundColor: '#34B772' }}
                onClick={handleAccept}
                disabled={joining}
              >
                {joining ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                )}
                Aceptar invitación
              </Button>
              <Button variant="outline" className="w-full" asChild>
                <Link to="/">Cancelar</Link>
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-center text-sm text-slate-600 mb-4">
                Para unirte al equipo, primero debes iniciar sesión o crear una cuenta con el email: <strong>{invitation?.email}</strong>
              </p>
              <Button 
                className="w-full" 
                style={{ backgroundColor: '#34B772' }}
                onClick={handleAccept}
              >
                <LogIn className="w-4 h-4 mr-2" />
                Iniciar sesión para unirme
              </Button>
              <Button variant="outline" className="w-full" asChild>
                <Link to={`/register?email=${encodeURIComponent(invitation?.email || '')}`}>
                  Crear cuenta nueva
                </Link>
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default JoinTeamPage;
